import * as React from 'react'
import { createRef, useMemo } from 'react'

import { Formik } from 'formik'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'

import { ENTITY_TYPES } from '@sales-center/common'
import { IAddOpportunityFormModel } from '../typings'
import { initialValues, useValidate } from '../formik'
import LinearProgress from '@mui/material/LinearProgress'
import { useData, useGetOpportunityTitle, useOnSubmit } from '../hooks'
import { customValuesToFormModel } from '@user/components/CustomFields'
import { AddOpportunityForm } from '../AddOpportunityForm/AddOpportunityForm'
import { SOLD_SERVICES_CONTENT_MODE } from '@user/components/Opportunities/SoldServicesDrawer/SoldServicesContent/typings'

import { useSelectCustomFieldData } from '@user/store/customFields'
import { useGetLeadOppProductsOnMount } from '@user/store/leadOppProducts'
import { selectLeadDetail, useGetLeadDetailOnMount } from '@user/store/lead'
import { pestNamesToIds, selectAllTargetPests } from '@user/store/targetPests'
import { useGetEntityLocations } from '@user/store/entityLocations/hooks/useGetEntityLocations'
import { useNoteCodes } from '@user/components/Opportunities/AddOpportunityPage/AddOpportunityForm/hooks/useNoteCodes'
import { useGetSalesFunnelData } from '@user/components/Settings/SalesFunnels/hooks'

type Props = {
  leadId: number
}

export function AddOpportunityFromLead(props: Props) {
  const { leadId } = props
  const dataLoading = useData()
  const leadLoadStatus = useGetLeadDetailOnMount(leadId)
  const leadDetail = useSelector(selectLeadDetail)

  const productsLoadStatus = useGetLeadOppProductsOnMount(
    leadId,
    ENTITY_TYPES.Lead,
  )

  const { loading, billTo, serviceLocations } = useGetEntityLocations(
    ENTITY_TYPES.Lead,
    leadId,
  )

  const title = useGetOpportunityTitle(billTo)

  const allTargetPests = useSelector(selectAllTargetPests)

  const { customFields } = useSelectCustomFieldData()

  const { noteCodes } = useNoteCodes()

  const initialCustomFields = customValuesToFormModel(
    customFields,
    leadDetail?.customFieldValues,
  )
  const { firstStage } = useGetSalesFunnelData(
    ENTITY_TYPES.Opportunity,
    leadDetail?.salesFunnelId || 0,
  )

  const hydratedInitialValues = useMemo<
    IAddOpportunityFormModel | undefined
  >(() => {
    if (leadDetail) {
      const targetPestInput: string[] = pestNamesToIds(
        allTargetPests,
        leadDetail.targetPests,
      )

      return {
        ...initialValues,
        billTo: billTo,
        billToExternalLinkId: billTo?.externalLinkId ?? '',
        campaign: leadDetail.campaign,
        campaignId: leadDetail.campaignId,
        customFields: initialCustomFields,
        leadSource: leadDetail.leadSource,
        leadSourceId: leadDetail.leadSourceId,
        ownerId: leadDetail.assigneeId?.toString() ?? 'unassigned',
        salesFunnelId: leadDetail.salesFunnelId.toString(),
        salesTeamId: leadDetail.salesTeamId.toString(),
        salesFunnelStageId: leadDetail.salesFunnelStageId,
        serviceLocations: serviceLocations,
        sourceEntityId: leadId,
        targetPests: targetPestInput,
        leadId: leadId,
        referral: leadDetail.referralSummary,
        nextActionDate: leadDetail.nextActionDate,
        externalLocationId: leadDetail.externalLocationId,
        opportunityStageId: firstStage?.id.toString() || '',
        addNoteToPestPac: Boolean(leadDetail.externalLocationId),
        preferredMethodOfContact:
          leadDetail.primaryContact?.contactMethod ??
          leadDetail?.serviceContact?.contactMethod,
        preferredTimeOfContact:
          leadDetail.primaryContact?.contactTime ??
          leadDetail?.serviceContact?.contactTime,
        serviceLocation:
          serviceLocations.length === 1
            ? serviceLocations[0].id.toString()
            : '',
        noteCode: Boolean(leadDetail.externalLocationId)
          ? noteCodes.find((code) => code.isDefault)?.name
          : '',
      }
    }
  }, [
    leadDetail,
    allTargetPests,
    billTo,
    initialCustomFields,
    serviceLocations,
    leadId,
    firstStage?.id,
    noteCodes,
  ])

  const externalSvcLocId = serviceLocations?.[0]?.externalLinkId ?? ''

  const showForm =
    !dataLoading &&
    !loading &&
    leadLoadStatus !== 'pending' &&
    billTo &&
    serviceLocations &&
    productsLoadStatus !== 'pending' &&
    !!hydratedInitialValues

  const onSubmit = useOnSubmit({
    leadId,
    entityType: ENTITY_TYPES.Lead,
  })

  const ref = createRef<HTMLDivElement>()
  const { validate } = useValidate()

  if (leadLoadStatus === 'fail') {
    return <Navigate to="/opportunities/addOpportunity" />
  }

  return showForm ? (
    <Formik
      onSubmit={onSubmit}
      validate={validate}
      initialValues={hydratedInitialValues}
    >
      <AddOpportunityForm
        entityId={leadId}
        entityBranchId={leadDetail?.branchId}
        billTo={billTo}
        externalSvcLocId={externalSvcLocId}
        serviceLocations={serviceLocations}
        serviceLocationsLoading={loading}
        innerRef={ref}
        title={title}
        entityType={ENTITY_TYPES.Lead}
        type={SOLD_SERVICES_CONTENT_MODE.ConvertLead}
        noteCodes={noteCodes}
      />
    </Formik>
  ) : (
    <LinearProgress />
  )
}
